import { resolveApiUrl, unwrapStubbedResponse } from "@/lib/api";
import { AUTH_EVENTS, getActiveArtistId, getAuthToken } from "@/api/client";

const MAX_SNIPPET_LENGTH = 400;

function snippetFromBody(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) {
    return "<no response body>";
  }
  return trimmed.length > MAX_SNIPPET_LENGTH ? `${trimmed.slice(0, MAX_SNIPPET_LENGTH)}...` : trimmed;
}

type ApiErrorOptions = {
  status: number;
  statusText: string;
  contentType: string;
  snippet: string;
  url: string;
  payload?: unknown;
};

export class ApiError extends Error {
  status: number;
  statusText: string;
  contentType: string;
  snippet: string;
  url: string;
  payload?: unknown;

  constructor(message: string, options: ApiErrorOptions) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.statusText = options.statusText;
    this.contentType = options.contentType;
    this.snippet = options.snippet;
    this.url = options.url;
    this.payload = options.payload;
  }
}

function buildApiError(
  message: string,
  response: Response,
  body: string,
  url: string,
  payload?: unknown,
): ApiError {
  const contentType = response.headers.get("content-type") ?? "";
  return new ApiError(message, {
    status: response.status,
    statusText: response.statusText,
    contentType,
    snippet: snippetFromBody(body),
    url,
    payload,
  });
}

export async function fetcher<T>(path: string): Promise<T> {
  const url = resolveApiUrl(path);
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const artistId = getActiveArtistId();
  if (artistId) {
    headers["X-Artist-Id"] = artistId;
  }
  const response = await fetch(url, { headers });
  const body = await response.text().catch(() => "");
  const contentType = response.headers.get("content-type") ?? "";
  const isJsonResponse = contentType.toLowerCase().includes("application/json");

  if (!response.ok) {
    if (response.status === 401) {
      if (typeof window !== "undefined") {
        const w = window as typeof window & { __auth401Notified?: boolean; __auth401Timer?: number };
        if (!w.__auth401Notified) {
          w.__auth401Notified = true;
          if (w.__auth401Timer) {
            window.clearTimeout(w.__auth401Timer);
          }
          w.__auth401Timer = window.setTimeout(() => {
            w.__auth401Notified = false;
            w.__auth401Timer = undefined;
          }, 1500);
          try {
            window.dispatchEvent(new CustomEvent(AUTH_EVENTS.unauthorized, { detail: { url } }));
          } catch {
            window.dispatchEvent(new Event(AUTH_EVENTS.unauthorized));
          }
        }
      }
    }
    let parsedPayload: unknown | undefined;
    if (isJsonResponse && body) {
      try {
        parsedPayload = JSON.parse(body);
      } catch {
        // ignore
      }
    }
    throw buildApiError(
      `Failed to fetch ${url}: ${response.status} ${response.statusText}`,
      response,
      body,
      url,
      parsedPayload,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!isJsonResponse) {
    throw buildApiError(
      `Response from ${url} is not JSON (content-type=${contentType || "unknown"}).`,
      response,
      body,
      url,
    );
  }

  let parsed: unknown;
  try {
    parsed = body ? JSON.parse(body) : {};
  } catch (err) {
    throw buildApiError(
      `Failed to parse JSON from ${url}: ${(err as Error).message}`,
      response,
      body,
      url,
    );
  }

  return unwrapStubbedResponse<T>(parsed, path);
}
